import MiniSearch from 'minisearch';
import type { OrganizationState } from '../types';
import type { SearchIndexResult } from './search-index-types';

interface SearchDocument {
  id: string;
  uuid: string;
  entity: string;
  name: string;
  content: string;
  party: string;
}

const NORWEGIAN_SUFFIXES = [
  'ene',
  'ene',
  'er',
  'ing',
  'inger',
  'lig',
  'lige',
  'isk',
  'iske',
  'elsen',
  'else',
  'het',
  'en',
  'et',
  'ar',
];

function norwegianProcessTerm(term: string): string {
  const lower = term.toLowerCase();
  if (lower.length < 4) return lower;
  for (const suffix of NORWEGIAN_SUFFIXES) {
    if (lower.length > suffix.length + 2 && lower.endsWith(suffix)) {
      return lower.slice(0, -suffix.length);
    }
  }
  return lower;
}

let index: MiniSearch<SearchDocument> | null = null;
let ready = false;

function createIndex(): MiniSearch<SearchDocument> {
  return new MiniSearch<SearchDocument>({
    fields: ['name', 'content', 'party'],
    storeFields: ['uuid', 'entity', 'name', 'party'],
    searchOptions: {
      boost: { name: 3, party: 1.5 },
      prefix: true,
      fuzzy: 0.2,
      combineWith: 'AND',
    },
    processTerm: norwegianProcessTerm,
  });
}

/**
 * Concatenate non-empty strings with spaces, matching backend StringUtil.concat()
 */
function concat(...parts: (string | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Resolve employee UUIDs to names
 */
function resolveEmployees(uuids: string[] | undefined, namesById: Record<string, string>): string {
  if (!uuids?.length) return '';
  return uuids
    .map((id) => namesById[id])
    .filter(Boolean)
    .join(', ');
}

/**
 * Resolve partner UUIDs to names
 */
function resolvePartners(uuids: string[] | undefined, namesById: Record<string, string>): string {
  if (!uuids?.length) return '';
  return uuids
    .map((id) => namesById[id])
    .filter(Boolean)
    .join(', ');
}

/**
 * Extract searchable documents from OrganizationState.
 * Content fields mirror the backend's getDocumentContent() + getKeywords() logic
 * so that local search matches the same things as server-side search.
 */
function extractDocuments(
  o: OrganizationState,
  singleUserView: boolean,
  employeeNamesById: Record<string, string>,
  partnerNamesById: Record<string, string>,
): SearchDocument[] {
  const docs: SearchDocument[] = [];

  // Employees — backend: address, notes, email, herNumber, hprNumber, expertise, phone, profession + "Person"
  for (const e of Object.values(o.employeesById)) {
    if (e.deleted) continue;
    const name = employeeNamesById[e.uuid] || '';
    const content = concat(
      e.address,
      e.notes,
      e.email,
      e.herNumber,
      e.hprNumber,
      e.expertise,
      e.phone,
      e.secondaryPhone,
      e.profession,
      e.speciality,
      'Person Ansatt',
    );
    docs.push({ id: `Employee::${e.uuid}`, uuid: e.uuid, entity: 'Employee', name, content, party: '' });
  }

  // Partners — backend: notes, industry, address, telephone + "Samarbeidspartner"
  for (const p of Object.values(o.partnersById)) {
    if (p.deleted) continue;
    const name = partnerNamesById[p.uuid] || '';
    const content = concat(p.notes, p.industry, p.address, p.phone, p.email, p.url, 'Samarbeidspartner');
    docs.push({ id: `Partner::${p.uuid}`, uuid: p.uuid, entity: 'Partner', name, content, party: '' });
  }

  // Assets — backend: userManual, manufacturer, maintenance, maintenanceInterval, number, competency, supplier, model, location, type + "Utstyr"
  for (const a of Object.values(o.assetsById)) {
    if (a.deleted) continue;
    const content = concat(
      a.number,
      a.manufacturer,
      a.model,
      a.location,
      a.supplier,
      a.userManual,
      a.maintenance,
      a.maintenanceInterval,
      a.competency,
      a.category,
      'Utstyr',
    );
    docs.push({ id: `Asset::${a.uuid}`, uuid: a.uuid, entity: 'Asset', name: a.name || '', content, party: '' });
  }

  // Tasks — backend: situation, procedures, assigned employee + "Oppgave"
  for (const t of Object.values(o.tasksById)) {
    if (t.deleted) continue;
    const entity = t.locked ? 'StartTask' : 'Task';
    const content = concat(t.situation, t.procedures, 'Oppgave Rutine');
    docs.push({ id: `${entity}::${t.uuid}`, uuid: t.uuid, entity, name: t.name || '', content, party: '' });
  }

  // Issues — backend: description, immediateMeasures, furtherMeasures, identifiedMeasures, notes + "Avvik"
  for (const i of Object.values(o.issuesById)) {
    if (i.deleted) continue;
    const content = concat(
      i.description,
      i.immediateMeasures,
      i.furtherMeasures,
      i.identifiedMeasures,
      i.notes,
      'Avvik',
    );
    const party = concat(
      employeeNamesById[i.reportedBy ?? ''],
      employeeNamesById[i.reportedTo ?? ''],
      employeeNamesById[i.processedBy ?? ''],
    );
    docs.push({ id: `Issue::${i.uuid}`, uuid: i.uuid, entity: 'Issue', name: i.name || '', content, party });
  }

  // Functions — backend: responsibility, assigned employees, type + "Funksjon"
  if (!singleUserView) {
    for (const f of Object.values(o.functionsById)) {
      if (f.deleted) continue;
      const content = concat(f.description, f.responsibility, 'Funksjon Ansvarsområde');
      const party = resolveEmployees(f.employees, employeeNamesById);
      docs.push({
        id: `Function::${f.uuid}`,
        uuid: f.uuid,
        entity: 'Function',
        name: f.name || '',
        content,
        party,
      });
    }
  }

  // Contracts — backend: content, employees, partners + "Avtale"
  for (const c of Object.values(o.contractsById)) {
    if (c.deleted) continue;
    const content = concat(c.content, 'Avtale Kontrakt');
    const party = concat(
      resolveEmployees(c.employees, employeeNamesById),
      resolvePartners(c.partners, partnerNamesById),
    );
    docs.push({ id: `Contract::${c.uuid}`, uuid: c.uuid, entity: 'Contract', name: c.name, content, party });
  }

  // Documents — backend: content + "Dokument"
  for (const d of Object.values(o.documentsById)) {
    if (d.deleted) continue;
    const content = concat(d.content, 'Dokument Måldokument');
    docs.push({
      id: `Document::${d.uuid}`,
      uuid: d.uuid,
      entity: 'Document',
      name: d.name || '',
      content,
      party: '',
    });
  }

  // Guidelines — backend: content + "Retningslinje"
  for (const g of Object.values(o.guidelinesById)) {
    if (g.deleted) continue;
    const content = concat(g.content, 'Retningslinje');
    docs.push({
      id: `Guideline::${g.uuid}`,
      uuid: g.uuid,
      entity: 'Guideline',
      name: g.name || '',
      content,
      party: '',
    });
  }

  // Reports — backend: content + "Rapport Referat"
  for (const r of Object.values(o.reportsById)) {
    if (r.deleted) continue;
    const content = concat(r.content, 'Rapport Referat');
    docs.push({
      id: `Report::${r.uuid}`,
      uuid: r.uuid,
      entity: 'Report',
      name: r.name || '',
      content,
      party: '',
    });
  }

  // Constitutional documents — backend: content, employees, partners + "Stiftelsesdokument"
  for (const d of Object.values(o.constitutionalDocumentsById)) {
    if (d.deleted) continue;
    const content = concat(d.content, 'Stiftelsesdokument');
    const party = concat(
      resolveEmployees(d.employees, employeeNamesById),
      resolvePartners(d.partners, partnerNamesById),
    );
    docs.push({
      id: `ConstitutionalDocument::${d.uuid}`,
      uuid: d.uuid,
      entity: 'ConstitutionalDocument',
      name: d.name || '',
      content,
      party,
    });
  }

  // Computers — backend: location, type, text + "Datamaskin"
  for (const c of Object.values(o.computersById)) {
    if (c.deleted) continue;
    const content = concat(c.location, c.type, c.notes, 'Datamaskin');
    docs.push({
      id: `Computer::${c.uuid}`,
      uuid: c.uuid,
      entity: 'Computer',
      name: c.name || '',
      content,
      party: '',
    });
  }

  // Networks — backend: nhnDescription, otherDescription, connectionType + "Nettverk"
  for (const n of Object.values(o.networksById)) {
    if (n.deleted) continue;
    const content = concat(n.nhnDescription, n.otherDescription, n.connectionType, 'Nettverk');
    docs.push({
      id: `Network::${n.uuid}`,
      uuid: n.uuid,
      entity: 'Network',
      name: n.name || '',
      content,
      party: '',
    });
  }

  // External connections — backend: description, type + "Ekstern forbindelse"
  for (const e of Object.values(o.externalConnectionsById)) {
    if (e.deleted) continue;
    const content = concat(e.description, e.type, 'Ekstern forbindelse');
    docs.push({
      id: `ExternalConnection::${e.uuid}`,
      uuid: e.uuid,
      entity: 'ExternalConnection',
      name: e.name || '',
      content,
      party: '',
    });
  }

  // Personal data items — backend: type, storageMedium, purpose, legalBasis + "Personopplysninger"
  for (const p of Object.values(o.personalDataItemsById)) {
    if (p.deleted) continue;
    const content = concat(p.type, p.storageMedium, p.purpose, p.legalBasis, p.notes, 'Personopplysninger');
    docs.push({
      id: `PersonalDataItem::${p.uuid}`,
      uuid: p.uuid,
      entity: 'PersonalDataItem',
      name: p.name || '',
      content,
      party: '',
    });
  }

  // Risk assessments — backend: description, riskDescription, measures + "Risikovurdering"
  for (const r of Object.values(o.riskAssessmentsById)) {
    if (r.deleted) continue;
    const content = concat(r.description, r.riskDescription, r.measures, 'Risikovurdering');
    docs.push({
      id: `RiskAssessment::${r.uuid}`,
      uuid: r.uuid,
      entity: 'RiskAssessment',
      name: r.name || '',
      content,
      // party fjernet: determinedBy/responsibleBy utgår (dabih-web#12750). Etter
      // backend-migreringen (dabih-api#4502) ligger evt. navn i measures → content.
      party: '',
    });
  }

  // Substances — backend: applications, notes + "Stoff Stoffkartotek"
  for (const s of Object.values(o.substancesById)) {
    if (s.deleted) continue;
    const content = concat(s.applications, s.notes, s.location, 'Stoff Stoffkartotek');
    const party = partnerNamesById[s.supplierUuid ?? ''] || '';
    docs.push({
      id: `Substance::${s.uuid}`,
      uuid: s.uuid,
      entity: 'Substance',
      name: s.name || '',
      content,
      party,
    });
  }

  // Meeting occurrences — backend: agenda + "Møte"
  for (const m of o.meetingOccurrences) {
    const content = concat(m.agenda, 'Møte');
    docs.push({
      id: `MeetingOccurrence::${m.uuid}`,
      uuid: m.uuid,
      entity: 'MeetingOccurrence',
      name: m.name || '',
      content,
      party: '',
    });
  }

  // Event occurrences — backend: description, notes + "Hendelse"
  for (const e of o.eventOccurrences) {
    const content = concat(e.description, e.notes, 'Hendelse');
    docs.push({
      id: `EventOccurrence::${e.uuid}`,
      uuid: e.uuid,
      entity: 'EventOccurrence',
      name: e.name || '',
      content,
      party: '',
    });
  }

  return docs;
}

export function rebuildSearchIndex(
  organization: OrganizationState,
  singleUserView: boolean,
  employeeNamesById: Record<string, string>,
  partnerNamesById: Record<string, string>,
): void {
  const start = performance.now();
  const docs = extractDocuments(organization, singleUserView, employeeNamesById, partnerNamesById);

  index = createIndex();
  index.addAll(docs);
  ready = true;

  const elapsed = Math.round(performance.now() - start);
  console.log(`[SearchIndex] Indexed ${docs.length} documents in ${elapsed}ms`);
}

export function searchIndex(query: string, limit = 200): SearchIndexResult[] {
  if (!index) return [];
  return index
    .search(query)
    .slice(0, limit)
    .map((hit) => ({
      uuid: hit.uuid,
      entity: hit.entity,
      name: hit.name,
      party: hit.party,
      score: hit.score,
    }));
}

export function isIndexReady(): boolean {
  return ready;
}
